import { HttpErrorResponse, HttpRequest, HttpHandlerFn, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthenticateService } from '../services/authenticate.service';
import { UserService } from '../services/user.service';

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const router = inject(Router);
  const userService = inject(UserService);
  const authService = inject(AuthenticateService);
  const token = localStorage.getItem('access_token');

  let authReq = req;
  if (token) {
    authReq = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`),
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      const status = error.status;
      const firstErrorReason = Array.isArray(error.error?.errors) ? error.error.errors[0]?.reason : undefined;
      const msg = error.error?.detail || firstErrorReason || error.error?.reason || error.error?.message || error.error?.title || error.message || 'An unexpected error occurred.';

      switch (status) {
        case 400:
          console.error(`[API 400] Bad Request: ${msg}`);
          messageService.add({
            severity: 'error',
            summary: '錯誤的請求',
            detail: msg,
            sticky: false,
          });
          break;
        case 401:
          console.error(`[API 401] Unauthorized: ${msg}`);
          // 如果是登入失敗，顯示 API 回傳的錯誤訊息（例如：帳號或密碼錯誤）
          if (req.url.includes('/Login')) {
            console.log('Login failed');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            messageService.add({
              severity: 'error',
              summary: '登入失敗',
              detail: msg,
              sticky: false,
            });
            return throwError(() => error);
          }

          // 如果是刷新 Token 本身失敗，代表已完全過期，執行登出並提示連線逾時
          if (req.url.includes('/RefreshToken')) {
            console.log('RefreshToken failed');
            handleLogout(messageService, router, userService);
            return throwError(() => error);
          }

          if (localStorage.getItem('access_token')) {
            console.log('handle401Error');
            return handle401Error(req, next, messageService, router, userService, authService);
          } else {
            messageService.add({
              severity: 'error',
              summary: '授權失敗',
              detail: msg,
              sticky: false,
            });
          }
          break;
        case 403:
          console.error(`[API 403] Forbidden: ${msg}`);
          messageService.add({
            severity: 'error',
            summary: '權限不足',
            detail: msg,
            sticky: false,
          });
          break;
        case 404:
          console.error(`[API 404] Not Found: ${msg}`);
          messageService.add({
            severity: 'error',
            summary: '找不到資源',
            detail: msg,
            sticky: false,
          });
          break;
        case 415:
          console.error(`[API 415] Unsupported Media Type: ${msg}`);
          messageService.add({
            severity: 'error',
            summary: '請求內容格式錯誤',
            detail: msg,
            sticky: false,
          });
          break;
        case 500:
          console.error(`[API 500] Server Error: ${msg}`);
          messageService.add({
            severity: 'error',
            summary: '伺服器錯誤',
            detail: msg,
            sticky: false,
          });
          break;
        default:
          console.error(`[API ${status}] Unknown Error: ${msg}`);
          messageService.add({
            severity: 'error',
            summary: '系統異常，請聯繫系統管理員',
            detail: msg,
            sticky: false,
          });
          break;
      }
      return throwError(() => error);
    })
  );
};

const handle401Error = (req: HttpRequest<any>, next: HttpHandlerFn, messageService: MessageService, router: Router, userService: UserService, authService: AuthenticateService) => {

  if (!isRefreshing) {
    console.log('start handle401Error');
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const accessToken = localStorage.getItem('access_token') || '';
    const refreshToken = localStorage.getItem('refresh_token') || '';

    return authService.apiPostRefreshToken({ accessToken, refreshToken }).pipe(
      switchMap((response) => {
        isRefreshing = false;
        refreshTokenSubject.next(response.accessToken);
        return next(
          req.clone({
            headers: req.headers.set('Authorization', `Bearer ${response.accessToken}`),
          })
        );
      }),
      catchError((err) => {
        isRefreshing = false;
        handleLogout(messageService, router, userService);
        return throwError(() => err);
      })
    );
  } else {
    // 正在刷新中，將請求加入隊列排隊
    console.log('handle401Error isRefreshing');
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        return next(
          req.clone({
            headers: req.headers.set('Authorization', `Bearer ${token}`),
          })
        );
      })
    );
  }
};

const handleLogout = (messageService: MessageService, router: Router, userService: UserService) => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('access_token_expired_at');
  // 清除 user profile signal，確保 OmniMqttService 的 effect 能觸發 disconnect()
  userService.clearProfile();
  messageService.add({
    severity: 'error',
    summary: '連線逾時',
    detail: '帳號已過期，請重新登入系統',
    sticky: false,
  });
  router.navigate(['/login']);
};
